import { ArrowRight, Sparkles, MapPin, BedDouble, Bath, Ruler, Car } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

const HERO_PROPERTY_IMAGE = "https://images.unsplash.com/photo-1754325899655-5e9b7bf05cdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjBuZXclMjBob21lJTIwcmVhbCUyMGVzdGF0ZSUyMGxpc3RpbmclMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3NzM2OTkxMDh8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  return (
    <section id="inicio" className="relative min-h-screen flex items-center pt-16 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-purple/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-pink/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6 sm:mb-8 border border-white/20">
            <Sparkles className="w-4 h-4 text-brand-purple" />
            <span>Plataforma SaaS para Agentes Inmobiliarios</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
            Crea landing pages profesionales para tus propiedades{" "}
            <span className="bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent">
              en minutos
            </span>
          </h1>

          <p className="text-base sm:text-xl text-gray-300 leading-relaxed mb-6 sm:mb-10 max-w-2xl mx-auto">
            Sin conocimientos técnicos. Personaliza colores, agrega tracking pixels, 
            recibe leads en tu dashboard y por email. Todo en una plataforma diseñada 
            para vendedores inmobiliarios.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12">
              <Button
                size="lg"
                className="bg-brand-purple hover:bg-brand-pink text-white text-lg px-8 py-6 group w-full sm:w-auto"
                data-testid="cta-register"
                onClick={(e) => { 
                  e.preventDefault();
                  if (window.firePixelAndNavigate) {
                    window.firePixelAndNavigate('Lead', { content_name: 'CTA Comenzar Ahora' }, '/register');
                  } else {
                    window.location.href = '/register';
                  }
                }}
              >
                Comenzar Ahora
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            <Link to="/precios">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 w-full sm:w-auto"
                data-testid="cta-pricing"
              >
                Ver Planes y Precios
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">5 min</div>
              <div className="text-sm text-gray-400">Crear landing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-sm text-gray-400">Personalizable</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">0</div>
              <div className="text-sm text-gray-400">Código requerido</div>
            </div>
          </div>
        </div>

        {/* Realistic Landing Page Preview */}
        <div className="mt-16 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent z-10 pointer-events-none" style={{ height: '30%', bottom: 0, top: 'auto' }}></div>
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-5xl mx-auto border border-white/20">
            {/* Browser chrome */}
            <div className="bg-gray-100 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-500 ml-4 font-mono">
                inmopages.app/propiedades/casa-en-venta-en-colinas-de-san-javier
              </div>
            </div>
            
            {/* Realistic landing page content */}
            <div className="relative">
              {/* Hero image with overlay */}
              <div className="relative h-72 sm:h-80 md:h-96">
                <img
                  src={HERO_PROPERTY_IMAGE}
                  alt="Casa en venta en Colinas de San Javier"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Floating buttons top right */}
                <div className="absolute top-3 right-3 flex gap-2">
                  <div className="bg-white/90 backdrop-blur text-gray-700 text-[10px] px-2.5 py-1.5 rounded-md font-medium flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
                    Compartir
                  </div>
                  <div className="bg-green-600 text-white text-[10px] px-2.5 py-1.5 rounded-md font-medium flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                    WhatsApp
                  </div>
                </div>
                
                {/* Property info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  {/* Badge */}
                  <span className="inline-block bg-red-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded mb-2 uppercase tracking-wide">
                    Casa en Venta
                  </span>
                  
                  {/* Title */}
                  <h2 className="text-white text-lg sm:text-2xl md:text-3xl font-bold mb-1 leading-tight">
                    Casa en venta en Colinas de San Javier
                  </h2>
                  
                  {/* Location */}
                  <div className="flex items-center text-white/80 text-xs sm:text-sm mb-3">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                    Guadalajara, Jalisco
                  </div>
                  
                  {/* Price and specs row */}
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <p className="text-white/60 text-[10px] sm:text-xs">Precio</p>
                      <p className="text-white text-xl sm:text-2xl md:text-3xl font-bold">$7,490,000</p>
                    </div>
                    
                    {/* Specs */}
                    <div className="flex gap-2 sm:gap-3">
                      {[
                        { icon: BedDouble, value: "4", label: "Rec." },
                        { icon: Bath, value: "3", label: "Baños" },
                        { icon: Ruler, value: "580", label: "m²" },
                        { icon: Car, value: "4", label: "Est." }
                      ].map((spec, i) => (
                        <div key={i} className="flex items-center gap-1 bg-white/15 backdrop-blur-sm rounded-md px-2 py-1.5 text-white">
                          <spec.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-[10px] sm:text-xs font-medium">{spec.value} {spec.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
